import { removeResizingStyles } from 'apps/studio/components/pdfViewer/elements/ResizeHandle'
import { guid } from 'apps/builder/lib/utils'
import {
  createScreenData,
  STATIC_TABLE_TITLE_HEIGHT,
  STATIC_TABLE_ROW_HEIGHT,
} from 'apps/studio/lib/utils/entities'

export const MIN_RESIZE_WIDTH = 8
export const MIN_RESIZE_HEIGHT = 8

// Minimum column width / row height when dragging a table divider. Matches the chevron
// quick-menu button size (1.25em) plus padding at the 16px root font-size = 22px, so a
// cell can never shrink smaller than its control. Not perfect, but works for now.
export const MIN_TABLE_CELL_SIZE = 22

export const calculatePagePosition = ({
  draggablePosition,
  droppablePosition,
  guidelinePosition = {},
  scaleFactor = 1,
}) => {
  const {
    x: draggableX,
    y: draggableY,
    width: draggableWidth,
    height: draggableHeight,
  } = draggablePosition
  const {
    left: droppableX,
    top: droppableY,
    width: droppableWidth,
    height: droppableHeight,
  } = droppablePosition

  let xCoordinate = Math.round((draggableX - droppableX) / scaleFactor)
  let yCoordinate = Math.round((draggableY - droppableY) / scaleFactor)

  // if there's a guideline, snap to it (negative values account for "above" or "left")
  const { x: guidelineX = null, y: guidelineY = null } = guidelinePosition
  if (guidelineX !== null) {
    if (guidelineX >= 0 && !Object.is(guidelineX, -0)) {
      xCoordinate = Math.round(guidelineX / scaleFactor)
    } else {
      xCoordinate = Math.round(
        (Math.abs(guidelineX) - draggableWidth * scaleFactor) / scaleFactor
      )
    }
  }
  if (guidelineY !== null) {
    if (guidelineY >= 0 && !Object.is(guidelineY, -0)) {
      yCoordinate = Math.round(guidelineY / scaleFactor)
    } else {
      yCoordinate = Math.round(
        (Math.abs(guidelineY) - draggableHeight * scaleFactor) / scaleFactor
      )
    }
  }

  const x2Coordinate = Math.round(xCoordinate + draggableWidth)
  const y2Coordinate = Math.round(yCoordinate + draggableHeight)

  const onPage =
    xCoordinate >= 0 &&
    yCoordinate >= 0 &&
    x2Coordinate <= droppableWidth / scaleFactor &&
    y2Coordinate <= droppableHeight / scaleFactor

  return {
    x: xCoordinate,
    y: yCoordinate,
    width: draggableWidth,
    height: draggableHeight,
    onPage,
  }
}

export const calculateResizePosition = ({
  delta,
  dropTarget,
  originalPosition,
  handleLocation,
  scale,
}) => {
  const { x: deltaX, y: deltaY } = delta
  const changeInX = Math.round(deltaX / scale)
  const changeInY = Math.round(deltaY / scale)

  const { width: containerWidth, height: containerHeight } =
    dropTarget.rect || {}

  const maxWidth = containerWidth / scale
  const maxHeight = containerHeight / scale

  const changedHeightFromTop = Math.max(
    Math.min(
      originalPosition.height - changeInY,
      originalPosition.y + originalPosition.height
    ),
    MIN_RESIZE_HEIGHT
  )

  const changedHeightFromBottom = Math.max(
    Math.min(
      originalPosition.height + changeInY,
      maxHeight - originalPosition.y
    ),
    MIN_RESIZE_HEIGHT
  )

  const changedWidthFromLeft = Math.max(
    Math.min(
      originalPosition.width - changeInX,
      originalPosition.width + originalPosition.x
    ),
    MIN_RESIZE_WIDTH
  )

  const changedWidthFromRight = Math.max(
    Math.min(originalPosition.width + changeInX, maxWidth - originalPosition.x),
    MIN_RESIZE_WIDTH
  )

  const changedXFromLeft = Math.min(
    Math.max(originalPosition.x + changeInX, 0),
    originalPosition.x + originalPosition.width - MIN_RESIZE_WIDTH
  )
  const changedXFromRight = originalPosition.x

  const changedYFromTop = Math.min(
    Math.max(originalPosition.y + changeInY, 0),
    originalPosition.y + originalPosition.height - MIN_RESIZE_HEIGHT
  )
  const changedYFromBottom = originalPosition.y

  const handlers = {
    'top-left': () => {
      return {
        y: changedYFromTop,
        x: changedXFromLeft,
        width: changedWidthFromLeft,
        height: changedHeightFromTop,
      }
    },
    'top-right': () => {
      return {
        y: changedYFromTop,
        x: changedXFromRight,
        width: changedWidthFromRight,
        height: changedHeightFromTop,
      }
    },
    'bottom-left': () => {
      return {
        y: changedYFromBottom,
        x: changedXFromLeft,
        width: changedWidthFromLeft,
        height: changedHeightFromBottom,
      }
    },
    'bottom-right': () => {
      return {
        y: changedYFromBottom,
        x: changedXFromRight,
        width: changedWidthFromRight,
        height: changedHeightFromBottom,
      }
    },
  }

  return handlers[handleLocation]()
}

// guideline match range, in unscaled pixels
const TOLERANCE = 3

const isWithinTolerance = (v1, v2, tolerance = TOLERANCE) =>
  Math.abs(v1 - v2) <= tolerance

export const calculateGuidelinePosition = (
  draggedElementPosition,
  pageElements
) => {
  const { x, y, x2, y2 } = draggedElementPosition
  let xAlign = null
  let yAlign = null
  let xNear = null
  let yNear = null

  for (const pageElement of pageElements) {
    const { position } = pageElement
    if (!position) continue
    const { x: cx, y: cy, width, height } = position
    const cx2 = cx + width
    const cy2 = cy + height

    // exact matches, negative encodes "above" or "left"
    if (cx === x) {
      xAlign = cx
      continue
    }
    if (cx2 === x) {
      xAlign = cx2
      continue
    }
    if (cx === x2) {
      xAlign = -cx
      continue
    }
    if (cx2 === x2) {
      xAlign = -cx2
      continue
    }
    if (cy === y) {
      yAlign = cy
      continue
    }
    if (cy2 === y) {
      yAlign = cy2
      continue
    }
    if (cy === y2) {
      yAlign = -cy
      continue
    }
    if (cy2 === y2) {
      yAlign = -cy2
      continue
    }

    // near matches, negative encodes "above" or "left"
    if (!xAlign) {
      if (isWithinTolerance(cx, x)) {
        xNear = cx
      } else if (isWithinTolerance(cx2, x)) {
        xNear = cx2
      } else if (isWithinTolerance(cx, x2)) {
        xNear = -cx
      } else if (isWithinTolerance(cx2, x2)) {
        xNear = -cx2
      }
    }

    if (!yAlign) {
      if (isWithinTolerance(cy, y)) {
        yNear = cy
      } else if (isWithinTolerance(cy2, y)) {
        yNear = cy2
      } else if (isWithinTolerance(cy, y2)) {
        yNear = -cy
      } else if (isWithinTolerance(cy2, y2)) {
        yNear = -cy2
      }
    }

    // Break early if both exact X and Y alignments have been found
    if (xAlign && yAlign) {
      break
    }
  }
  return {
    xAlign,
    yAlign,
    xNear,
    yNear,
  }
}

export const handleDataInputDrop = (
  draggable,
  droppable,
  elements,
  calculatedPosition
) => {
  const { page, layout, existingScreenData } = droppable.data.current
  const { entryData } = draggable.data.current

  const { page_number: pageNumber } = page
  const newScreenData = createScreenData(pageNumber)

  const newPageElements = elements.filter(
    element =>
      element.page_number === pageNumber && element.layout_id === layout.id
  )

  const entry_guid = guid()
  const update = {
    entry: {
      ...entryData,
      guid: entry_guid,
      position: newPageElements.length,
      label: `New ${entryData.type}`,
      sheet_id: existingScreenData
        ? existingScreenData.sheet.id
        : newScreenData.sheet.id,
    },
    element: {
      page_number: pageNumber,
      page_id: page.id,
      layout_id: layout.id,
      entry_guid,
      position: calculatedPosition,
    },
  }

  const createElementPayload = existingScreenData
    ? update
    : { ...update, ...newScreenData, page: page, layout: layout }
  return createElementPayload
}

export const handlePdfElementDrop = (
  draggable,
  droppable,
  elements,
  calculatedPosition
) => {
  const { page, layout, existingScreenData } = droppable.data.current
  const {
    page: currentPage,
    layout: currentLayout,
    element,
  } = draggable.data.current
  const { page_number: pageNumber } = page
  const newScreenData = createScreenData(pageNumber)

  const nextScreenElements = elements.filter(
    element =>
      element.page_number === pageNumber && element.layout_id === layout.id
  )
  const currentScreenElements = elements.filter(
    element =>
      element.page_id === currentPage.id &&
      element.layout_id === currentLayout.id
  )
  const movedToNewPage = !existingScreenData || page.id !== currentPage.id
  let sectionToDelete
  if (currentScreenElements.length === 1 && movedToNewPage) {
    // Only element on page is being moved to a new page delete the section/sheet it is being moved from
    sectionToDelete = currentLayout.section_id
  }

  const update = {
    entry: {
      guid: element.entry_guid,
      sheet_id: existingScreenData
        ? existingScreenData.sheet.id
        : newScreenData.sheet.id,
      ...(movedToNewPage
        ? { position: existingScreenData ? nextScreenElements.length : 0 }
        : {}),
    },
    element: {
      page_number: pageNumber,
      page_id: page.id,
      layout_id: layout.id,
      entry_guid: element.entry_guid,
      position: {
        ...element.position,
        x: calculatedPosition.x,
        y: calculatedPosition.y,
      },
    },
    sectionToDelete,
  }

  const updateElementPayload = existingScreenData
    ? update
    : { ...update, ...newScreenData, page: page, layout: layout }
  return updateElementPayload
}

export const handleResizeHandleDrop = (draggable, droppable, delta) => {
  const { position, handleLocation, entry, wrapperId } = draggable.data.current
  const { scaleFactor } = droppable.data.current
  const elementWrapper = document.getElementById(wrapperId)
  removeResizingStyles(elementWrapper)

  const newPosition = calculateResizePosition({
    delta,
    dropTarget: droppable,
    originalPosition: position,
    handleLocation,
    scale: scaleFactor,
  })

  const resizeElementPayload = {
    entry,
    element: {
      entry_guid: entry.guid,
      position: newPosition,
    },
  }
  return resizeElementPayload
}

export const handleTableResizeDrop = (draggable, droppable, delta) => {
  const { position, handleLocation, tableLayout, wrapperId } =
    draggable.data.current
  const { scaleFactor } = droppable.data.current
  const wrapperEl = document.getElementById(wrapperId)
  if (wrapperEl) removeResizingStyles(wrapperEl)
  const newPosition = calculateResizePosition({
    delta,
    dropTarget: droppable,
    originalPosition: position,
    handleLocation,
    scale: scaleFactor,
  })

  const { columns = [], rows = [] } = tableLayout
  const interiorOldH = Math.max(position.height - STATIC_TABLE_TITLE_HEIGHT, 1)
  const interiorNewH = Math.max(
    newPosition.height - STATIC_TABLE_TITLE_HEIGHT,
    1
  )
  const colScale = newPosition.width / position.width
  const rowScale = interiorNewH / interiorOldH

  const newColumns = columns.map(c => ({
    ...c,
    width: Math.max(Math.round(c.width * colScale), MIN_RESIZE_WIDTH),
  }))
  const newRows = rows.map(r => ({
    ...r,
    height: Math.max(
      Math.round((r.height ?? STATIC_TABLE_ROW_HEIGHT) * rowScale),
      MIN_RESIZE_HEIGHT
    ),
  }))

  // Keep cells flush with frame edges by recomputing from clamped column/row sums.
  const finalWidth = newColumns.reduce((s, c) => s + c.width, 0)
  const finalHeight =
    STATIC_TABLE_TITLE_HEIGHT + newRows.reduce((s, r) => s + r.height, 0)

  const leftAnchored =
    handleLocation === 'top-right' || handleLocation === 'bottom-right'
  const topAnchored =
    handleLocation === 'bottom-left' || handleLocation === 'bottom-right'

  const finalX = leftAnchored
    ? position.x
    : position.x + position.width - finalWidth
  const finalY = topAnchored
    ? position.y
    : position.y + position.height - finalHeight

  return {
    layout: {
      id: tableLayout.id,
      position: {
        x: finalX,
        y: finalY,
        width: finalWidth,
        height: finalHeight,
      },
      columns: newColumns,
      rows: newRows,
    },
  }
}

export const calculateTableDividerResize = ({ active, over, delta }) => {
  const { axis, index, tableLayout } = active.data.current
  const { scaleFactor = 1 } = over.data.current
  const { columns = [], rows = [], position: pos = {} } = tableLayout

  if (axis === 'col') {
    const pageWidth = over.rect.width / scaleFactor
    const original = columns[index].width
    const totalWidth = columns.reduce((s, c) => s + c.width, 0)
    const widthOfOthers = totalWidth - original
    const maxWidth = Math.max(
      pageWidth - pos.x - widthOfOthers,
      MIN_TABLE_CELL_SIZE
    )
    const newWidth = Math.max(
      MIN_TABLE_CELL_SIZE,
      Math.min(Math.round(original + delta.x / scaleFactor), maxWidth)
    )
    const newColumns = columns.map((c, i) =>
      i === index ? { ...c, width: newWidth } : c
    )

    return {
      layout: {
        id: tableLayout.id,
        position: { ...pos, width: widthOfOthers + newWidth },
        columns: newColumns,
        rows,
      },
    }
  }

  // axis === 'row'
  const pageHeight = over.rect.height / scaleFactor
  const original = rows[index].height
  const totalHeight = rows.reduce((s, r) => s + r.height, 0)
  const heightOfOthers = totalHeight - original
  const maxHeight = Math.max(
    pageHeight - pos.y - STATIC_TABLE_TITLE_HEIGHT - heightOfOthers,
    MIN_TABLE_CELL_SIZE
  )
  const newHeight = Math.max(
    MIN_TABLE_CELL_SIZE,
    Math.min(Math.round(original + delta.y / scaleFactor), maxHeight)
  )
  const newRows = rows.map((r, i) =>
    i === index ? { ...r, height: newHeight } : r
  )

  return {
    layout: {
      id: tableLayout.id,
      position: {
        ...pos,
        height: STATIC_TABLE_TITLE_HEIGHT + heightOfOthers + newHeight,
      },
      columns,
      rows: newRows,
    },
  }
}

export const handleTableDividerResizeDrop = ({ active, over, delta }) => {
  const { wrapperId } = active.data.current
  const wrapperEl = document.getElementById(wrapperId)
  if (wrapperEl) removeResizingStyles(wrapperEl)
  return calculateTableDividerResize({ active, over, delta })
}

export const handleTableMoveDrop = ({ active, over, delta, allSheets }) => {
  const { tableLayout, page: fromPage } = active.data.current
  const {
    page: toPage,
    layout: toRootLayout,
    scaleFactor = 1,
  } = over.data.current
  const samePage = toPage.id === fromPage.id
  const oldPosition = tableLayout.position || {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  }
  const tableWidth = oldPosition.width
  const tableHeight = oldPosition.height

  let newX
  let newY
  if (samePage) {
    newX = oldPosition.x + delta.x / scaleFactor
    newY = oldPosition.y + delta.y / scaleFactor
  } else {
    const activeRect = active.rect.current.translated
    const cursorScreenX = activeRect.left + activeRect.width / 2
    const cursorScreenY = activeRect.top
    newX = (cursorScreenX - over.rect.left) / scaleFactor - tableWidth / 2
    newY = (cursorScreenY - over.rect.top) / scaleFactor
  }

  const pageWidth = over.rect.width / scaleFactor
  const pageHeight = over.rect.height / scaleFactor
  newX = Math.max(0, Math.min(newX, pageWidth - tableWidth))
  newY = Math.max(0, Math.min(newY, pageHeight - tableHeight))

  const updatedLayout = {
    ...tableLayout,
    page_id: toPage.id,
    parent_layout_id: toRootLayout.id,
    position: { ...oldPosition, x: newX, y: newY },
  }

  if (samePage) {
    return { layout: updatedLayout, fromPage, toPage }
  }

  const outerSectionId = tableLayout.section_id
  const keyFieldSheet = allSheets.find(s => s.section_id === outerSectionId)
  const innerSectionId = keyFieldSheet?.multi_section
  const tableSheet = allSheets.find(s => s.section_id === innerSectionId)
  const updatedTableSheet = tableSheet
    ? { ...tableSheet, page_number: toPage.page_number }
    : null
  const updatedKeyFieldSheet = keyFieldSheet
    ? { ...keyFieldSheet, page_number: toPage.page_number }
    : null

  return {
    layout: updatedLayout,
    fromPage,
    toPage,
    tableSheet: updatedTableSheet,
    keyFieldSheet: updatedKeyFieldSheet,
    outerSectionId,
    innerSectionId,
  }
}

const focusAtEnd = element => {
  element.focus()

  const length = element.value.length
  element.setSelectionRange(length, length)
}

// select entry label in field panel, falling back to first textarea
export const selectActiveEntryLabel = (type, guid = '') => {
  const fieldName = guid
    ? document.getElementById(`label--longTextInput--${guid}`)
    : document
        .getElementById(`field-settings-panel--field`)
        .querySelector('textarea')

  if (fieldName) {
    if (fieldName.value.startsWith(`New ${type}`)) {
      fieldName.select()
    } else {
      focusAtEnd(fieldName)
    }
  }
}
